import type { JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMurajaahQueue } from '@/features/murajaah/hooks';
import { QUEUE_TIER_LABELS, queueTierBadgeVariant } from '@/features/murajaah/labels';
import { ScheduleSessionDialog } from '@/features/murajaah/schedule-session-dialog';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

/**
 * Murajaah Queue (Phase G-2, Job 16) - a computed, non-persisted
 * recommendation list (ADR-022 Addendum, Decision 7), deliberately
 * `murajaah_sessions:manage`-only. Student/Guardian never reach this
 * route: the nav item itself is capability-gated (`app-layout.tsx`), and
 * this page's own capability check is a second, independent line of
 * defense - the backend remains the real authority either way.
 */
export function MurajaahQueuePage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [schedulingStudentId, setSchedulingStudentId] = useState<string | null>(null);
  const canManage = useHasCapability('murajaah_sessions:manage');
  const { data, isLoading, isError } = useMurajaahQueue(page, PAGE_SIZE, canManage);

  if (!canManage) {
    return (
      <p className="p-4 text-sm text-destructive">Anda tidak memiliki akses ke halaman ini.</p>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Antrian Murajaah</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {isLoading && (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertDescription>Gagal memuat antrian murajaah.</AlertDescription>
            </Alert>
          )}

          {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
            <p className="text-sm text-muted-foreground">Antrian murajaah kosong.</p>
          )}

          {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
            <>
              <ul className="flex flex-col gap-2">
                {data.items.map((row) => (
                  <li
                    key={row.targetId}
                    className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium">
                        Surah {row.surah}: Ayat {row.startAyah}
                        {row.endAyah !== row.startAyah ? `-${row.endAyah}` : ''}
                      </span>
                      <Badge variant={queueTierBadgeVariant(row.tier)} className="w-fit">
                        {QUEUE_TIER_LABELS[row.tier]}
                      </Badge>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setSchedulingStudentId(row.studentId);
                      }}
                    >
                      Jadwalkan
                    </Button>
                  </li>
                ))}
              </ul>

              {data.total_pages > 1 && (
                <div className="flex items-center justify-between gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => {
                      setPage((current) => Math.max(1, current - 1));
                    }}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Halaman {data.page} dari {data.total_pages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= data.total_pages}
                    onClick={() => {
                      setPage((current) => Math.min(data.total_pages, current + 1));
                    }}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {schedulingStudentId !== null && (
        <ScheduleSessionDialog
          studentId={schedulingStudentId}
          open={schedulingStudentId !== null}
          onOpenChange={(next) => {
            if (!next) {
              setSchedulingStudentId(null);
            }
          }}
        />
      )}
    </div>
  );
}
